import { useState } from 'react';
import axios from "axios";

const DemoRequestForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    mobile: "",
    email: "",
    trading: "",
    segment: "",
    investment: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // ---- Validation rules ----
  const validateField = (name, value) => {
    switch (name) {
      case "firstname":
        if (!value.trim()) return "First name is required";
        if (!/^[A-Za-z\s]{2,30}$/.test(value.trim()))
          return "Enter a valid first name (letters only, min 2 chars)";
        return "";

      case "lastname":
        if (!value.trim()) return "Last name is required";
        if (!/^[A-Za-z\s]{2,30}$/.test(value.trim()))
          return "Enter a valid last name (letters only, min 2 chars)";
        return "";

      case "mobile":
        if (!value.trim()) return "Mobile number is required";
        if (!/^[6-9]\d{9}$/.test(value.trim()))
          return "Enter a valid 10-digit mobile number";
        return "";

      case "email":
        if (!value.trim()) return "Email address is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()))
          return "Enter a valid email address";
        return "";

      case "trading":
        if (!value) return "Please select an option";
        return "";

      case "segment":
        if (!value) return "Please select a segment";
        return "";

      case "investment":
        // optional field, no validation required
        return "";

      default:
        return "";
    }
  };

  const validateAll = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Restrict mobile input to digits only, max 10 chars
    const nextValue =
      name === "mobile" ? value.replace(/\D/g, "").slice(0, 10) : value;

    const updatedFormData = { ...formData, [name]: nextValue };
    setFormData(updatedFormData);

    // Validate this field instantly as the user types
    const error = validateField(name, nextValue);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateAll();
    setErrors(newErrors);
    setTouched({
      firstname: true,
      lastname: true,
      mobile: true,
      email: true,
      trading: true,
      segment: true,
      investment: true,
    });

    if (Object.keys(newErrors).length > 0) {
      return; // stop submission if there are validation errors
    }

    setLoading(true);
    try {
      await axios.post("https://api.optionbricks.in/send-email", formData);
      alert("Thank you for reaching out! Your request has been submitted.");
      setFormData({
        firstname: "",
        lastname: "",
        mobile: "",
        email: "",
        trading: "",
        segment: "",
        investment: "",
      });
      setErrors({});
      setTouched({});
    } catch (err) {
      console.error(err);
      alert("Failed to submit. Please try again.");
    }
    setLoading(false);
  };

  const showError = (field) => touched[field] && errors[field];

  const inputClass = (field) =>
    `w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition ${
      showError(field)
        ? "border-red-500 focus:ring-red-400 focus:border-red-500"
        : "border-gray-300 focus:ring-black focus:border-black"
    }`;

  return (
    <div className="max-w-lg mx-auto bg-transparent rounded-xl p-0 md:p-8 transition-shadow">
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="firstname"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              First Name
            </label>
            <input
              id="firstname"
              type="text"
              name="firstname"
              placeholder="First Name"
              value={formData.firstname}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("firstname")}
            />
            {showError("firstname") && (
              <p className="text-red-500 text-sm mt-1">{errors.firstname}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="lastname"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Last Name
            </label>
            <input
              id="lastname"
              type="text"
              name="lastname"
              placeholder="Last Name"
              value={formData.lastname}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("lastname")}
            />
            {showError("lastname") && (
              <p className="text-red-500 text-sm mt-1">{errors.lastname}</p>
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="mobile"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Mobile Number
          </label>
          <input
            id="mobile"
            type="tel"
            name="mobile"
            placeholder="Mobile Number"
            value={formData.mobile}
            onChange={handleChange}
            onBlur={handleBlur}
            inputMode="numeric"
            maxLength={10}
            className={inputClass("mobile")}
          />
          {showError("mobile") && (
            <p className="text-red-500 text-sm mt-1">{errors.mobile}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Email Address
          </label>
          <input
            id="email"
            type="email"
            name="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            className={inputClass("email")}
          />
          {showError("email") && (
            <p className="text-red-500 text-sm mt-1">{errors.email}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="trading"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Are you currently trading?
          </label>
          <select
            id="trading"
            name="trading"
            value={formData.trading}
            onChange={handleChange}
            onBlur={handleBlur}
            className={inputClass("trading")}
          >
            <option value="">Are you currently trading?</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
          {showError("trading") && (
            <p className="text-red-500 text-sm mt-1">{errors.trading}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="segment"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Select Segment
            </label>
            <select
              id="segment"
              name="segment"
              value={formData.segment}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("segment")}
            >
              <option value="">Select Segment</option>
              <option value="Equity Cash/Intraday">Equity Cash/Intraday</option>
              <option value="Option (Call/Put)">Option (Call/Put)</option>
              <option value="Future (Derivatives)">Future (Derivatives)</option>
              <option value="Index">Index</option>
            </select>
            {showError("segment") && (
              <p className="text-red-500 text-sm mt-1">{errors.segment}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="investment"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Investment Amount
            </label>
            <select
              id="investment"
              name="investment"
              value={formData.investment}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("investment")}
            >
              <option value="">Investment Amount</option>
              <option value="Below ₹50,000">Below ₹50,000</option>
              <option value="₹50,000 - 1 Lakh">₹50,000 - 1 Lakh</option>
              <option value="1 Lakh - 3 Lakhs">1 Lakh - 3 Lakhs</option>
              <option value="3 Lakhs - 5 Lakhs">3 Lakhs - 5 Lakhs</option>
              <option value="5 Lakhs & above">5 Lakhs & above</option>
            </select>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full mt-5 py-3 bg-[#1e6091] text-white font-semibold rounded-full hover:bg-[#185076] transition-colors flex justify-center items-center gap-2 disabled:opacity-60"
        >
          {loading ? (
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              ></path>
            </svg>
          ) : (
            "Submit"
          )}
        </button>
      </form>
    </div>
  );
};

export default DemoRequestForm;